/**
 * Pure helpers behind the Particle Editor: the `particleSystem` component's
 * modules (Unity's Particle System modules), enabling / disabling them,
 * nested edits, bursts and sub-emitters, the scene commands that apply them,
 * undo coalescing for drags, and the isolated preview scene.
 *
 * The component format is engine/crates/prism_core/src/particles.rs: one
 * object per module; optional modules are off while absent or `enabled:
 * false` (the editor keeps a disabled module's settings, like Unity).
 */

import type { EditorCommand, EntityDesc, SceneDoc } from '../../lib/scene-doc.js';
import { copyEntities, slug, subtreeIds, uniqueWith } from '../../lib/scene-doc.js';
import type { IconName } from '../ui/icons.js';

export type ParticleSystem = Record<string, unknown>;
type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);
const clone = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T));

export interface ModuleDef {
  key: string;
  label: string;
  icon: IconName;
  /** Absent = off (it is added with `defaults` when enabled). */
  optional: boolean;
  /** Has an enable checkbox. */
  toggle: boolean;
  /** Value written when the module is first enabled. */
  defaults: Obj;
  hint: string;
}

/** The modules in Unity's order. */
export const PARTICLE_MODULES: readonly ModuleDef[] = [
  { key: 'main', label: 'Main', icon: 'particles', optional: false, toggle: false, defaults: {}, hint: 'Duration, looping, start values, simulation space.' },
  { key: 'emission', label: 'Emission', icon: 'sparkles', optional: false, toggle: true, defaults: {}, hint: 'Rate over time / distance and bursts.' },
  { key: 'shape', label: 'Shape', icon: 'cube', optional: false, toggle: true, defaults: {}, hint: 'Where particles are born and their start direction.' },
  { key: 'velocityOverLifetime', label: 'Velocity over Lifetime', icon: 'move', optional: true, toggle: true, defaults: { y: 1 }, hint: 'Adds a velocity (does not accumulate).' },
  {
    key: 'limitVelocityOverLifetime',
    label: 'Limit Velocity over Lifetime',
    icon: 'move',
    optional: true,
    toggle: true,
    defaults: { speed: 1, dampen: 0.5 },
    hint: 'Speed limit and drag.',
  },
  { key: 'forceOverLifetime', label: 'Force over Lifetime', icon: 'arrowUp', optional: true, toggle: true, defaults: { y: 1 }, hint: 'Acceleration (accumulates into velocity).' },
  {
    key: 'colorOverLifetime',
    label: 'Color over Lifetime',
    icon: 'pixel',
    optional: true,
    toggle: true,
    defaults: { color: { gradient: { alphaKeys: [[0, 1], [1, 0]] } } },
    hint: 'Multiplies the start color over each particle’s life.',
  },
  {
    key: 'sizeOverLifetime',
    label: 'Size over Lifetime',
    icon: 'scale',
    optional: true,
    toggle: true,
    defaults: { size: { curve: [[0, 1], [1, 0]] } },
    hint: 'Multiplies the start size over each particle’s life.',
  },
  { key: 'rotationOverLifetime', label: 'Rotation over Lifetime', icon: 'rotate', optional: true, toggle: true, defaults: { angularVelocity: 45 }, hint: 'Degrees per second.' },
  { key: 'noise', label: 'Noise', icon: 'wand', optional: true, toggle: true, defaults: { strength: 1, frequency: 0.5 }, hint: 'Turbulence: a smooth 3D noise field.' },
  {
    key: 'collision',
    label: 'Collision',
    icon: 'magnet',
    optional: true,
    toggle: true,
    defaults: { type: 'planes', planes: [], bounce: 0.5 },
    hint: 'Bounce on planes (entities’ XZ planes) or the physics world (play mode).',
  },
  { key: 'subEmitters', label: 'Sub Emitters', icon: 'layers', optional: true, toggle: true, defaults: { emitters: [] }, hint: 'Child systems that emit on birth, collision or death.' },
  {
    key: 'textureSheetAnimation',
    label: 'Texture Sheet Animation',
    icon: 'image',
    optional: true,
    toggle: true,
    defaults: { tiles: [2, 2] },
    hint: 'Flipbook frames from a grid in the texture.',
  },
  { key: 'renderer', label: 'Renderer', icon: 'eye', optional: false, toggle: false, defaults: {}, hint: 'Billboards or meshes, material / texture, blending, sorting.' },
];

export const MODULE_BY_KEY: ReadonlyMap<string, ModuleDef> = new Map(PARTICLE_MODULES.map((m) => [m.key, m]));

/** Default `particleSystem` for a new system (matches the Inspector's Add Component defaults). */
export const NEW_PARTICLE_SYSTEM: ParticleSystem = {
  main: { duration: 5, looping: true, startLifetime: 2, startSpeed: 2, startSize: 0.2, startColor: [1, 1, 1, 1], simulationSpace: 'world' },
  emission: { rateOverTime: 20 },
  shape: { type: 'cone', radius: 0.3, angle: 20 },
  colorOverLifetime: { color: { gradient: { alphaKeys: [[0, 1], [1, 0]] } } },
  renderer: { renderMode: 'billboard', blend: 'alpha' },
};

/** A sub-emitter child: a short one-shot burst. */
export const SUB_EMITTER_SYSTEM: ParticleSystem = {
  main: { duration: 1, looping: false, startLifetime: 0.6, startSpeed: 2, startSize: 0.1, simulationSpace: 'world', playOnAwake: false },
  emission: { rateOverTime: 0, bursts: [{ time: 0, count: 10 }] },
  shape: { type: 'sphere', radius: 0.05 },
  colorOverLifetime: { color: { gradient: { alphaKeys: [[0, 1], [1, 0]] } } },
  renderer: { renderMode: 'billboard', blend: 'additive' },
};

export function moduleValue(ps: ParticleSystem | undefined, key: string): Obj | undefined {
  const m = ps?.[key];
  return isObj(m) ? m : undefined;
}

/** Whether the module runs (always-on modules are always enabled). */
export function isModuleEnabled(ps: ParticleSystem | undefined, key: string): boolean {
  const def = MODULE_BY_KEY.get(key);
  if (!def || !def.toggle) return true;
  const m = moduleValue(ps, key);
  if (def.optional && !m) return false;
  return m?.enabled !== false;
}

/**
 * The component with the module turned on / off. Turning an optional module
 * on adds its defaults when absent; turning it off keeps its settings with
 * `enabled: false`. `enabled: true` is the default, so it is not written.
 */
export function setModuleEnabled(ps: ParticleSystem, key: string, on: boolean): ParticleSystem {
  const def = MODULE_BY_KEY.get(key);
  if (!def || !def.toggle) return ps;
  const current = moduleValue(ps, key);
  if (on) {
    const next: Obj = current ? { ...current } : clone(def.defaults);
    delete next.enabled;
    return { ...ps, [key]: next };
  }
  if (!current) return ps;
  return { ...ps, [key]: { ...current, enabled: false } };
}

/** Removes an optional module (back to absent) or resets an always-on one to its defaults. */
export function resetModule(ps: ParticleSystem, key: string, remove = false): ParticleSystem {
  const def = MODULE_BY_KEY.get(key);
  if (!def) return ps;
  const next = { ...ps };
  if (remove && def.optional) delete next[key];
  else next[key] = clone(def.optional ? def.defaults : (moduleValue(NEW_PARTICLE_SYSTEM, key) ?? {}));
  return next;
}

/** Value at a key path (`['main', 'startSize']`). */
export function getPath(value: unknown, path: readonly (string | number)[]): unknown {
  let cur = value;
  for (const k of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string | number, unknown>)[k];
  }
  return cur;
}

/**
 * A copy of `value` with `path` set (missing objects / arrays are created;
 * `undefined` deletes an object key). Never mutates its input.
 */
export function setPath<T>(value: T, path: readonly (string | number)[], next: unknown): T {
  if (path.length === 0) return next as T;
  const [head, ...rest] = path;
  const base: unknown = value;
  if (typeof head === 'number') {
    const arr = Array.isArray(base) ? [...(base as unknown[])] : [];
    arr[head] = setPath(arr[head], rest, next);
    return arr as T;
  }
  const obj: Obj = isObj(base) ? { ...base } : {};
  const child = setPath(obj[head!], rest, next);
  if (child === undefined) delete obj[head!];
  else obj[head!] = child;
  return obj as T;
}

/** The scene command that writes an entity's particle system. */
export function particleCommand(id: string, ps: ParticleSystem): EditorCommand {
  return { cmd: 'setComponent', id, key: 'particleSystem', value: ps };
}

/** Short summary shown on a module card's header. */
export function moduleSummary(ps: ParticleSystem, key: string): string {
  const m = moduleValue(ps, key) ?? {};
  const num = (v: unknown, unit = '') => (typeof v === 'number' ? `${Math.round(v * 100) / 100}${unit}` : Array.isArray(v) ? `${v.join('–')}${unit}` : '');
  switch (key) {
    case 'main': {
      const d = typeof m.duration === 'number' ? m.duration : 5;
      return `${d}s${m.looping === false ? '' : ' · loop'}${m.prewarm ? ' · prewarm' : ''}`;
    }
    case 'emission': {
      const bursts = Array.isArray(m.bursts) ? m.bursts.length : 0;
      const rate = m.rateOverTime === undefined ? '10/s' : typeof m.rateOverTime === 'number' || Array.isArray(m.rateOverTime) ? num(m.rateOverTime, '/s') : 'curve';
      return `${rate}${bursts ? ` · ${bursts} burst${bursts > 1 ? 's' : ''}` : ''}`;
    }
    case 'shape':
      return String(m.type ?? 'cone');
    case 'subEmitters': {
      const n = Array.isArray(m.emitters) ? m.emitters.length : 0;
      return n ? `${n} emitter${n > 1 ? 's' : ''}` : '';
    }
    case 'collision':
      return String(m.type ?? 'planes');
    case 'renderer':
      return `${m.renderMode ?? 'billboard'} · ${m.blend ?? 'alpha'}`;
    case 'textureSheetAnimation':
      return Array.isArray(m.tiles) ? `${m.tiles.join('×')}` : '';
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Bursts

export interface Burst {
  time?: number;
  count?: unknown;
  cycles?: number;
  interval?: number;
  probability?: number;
}

export function bursts(ps: ParticleSystem): Burst[] {
  const list = moduleValue(ps, 'emission')?.bursts;
  return Array.isArray(list) ? (list.filter(isObj) as Burst[]) : [];
}

function withBursts(ps: ParticleSystem, list: Burst[]): ParticleSystem {
  const emission: Obj = { ...(moduleValue(ps, 'emission') ?? {}) };
  if (list.length) emission.bursts = list;
  else delete emission.bursts;
  return { ...ps, emission };
}

/** Adds a burst after the last one (30 particles, once). */
export function addBurst(ps: ParticleSystem): ParticleSystem {
  const list = bursts(ps);
  const last = list[list.length - 1];
  const duration = (moduleValue(ps, 'main')?.duration as number | undefined) ?? 5;
  const time = last ? Math.min(duration, Math.round(((last.time ?? 0) + 0.5) * 100) / 100) : 0;
  return withBursts(ps, [...list, { time, count: 30 }]);
}

export function updateBurst(ps: ParticleSystem, index: number, patch: Partial<Burst>): ParticleSystem {
  const list = bursts(ps).map((b, i) => {
    if (i !== index) return b;
    const next: Obj = { ...b, ...patch };
    for (const [k, v] of Object.entries(patch)) if (v === undefined) delete next[k];
    return next as Burst;
  });
  return withBursts(ps, list);
}

export function removeBurst(ps: ParticleSystem, index: number): ParticleSystem {
  return withBursts(
    ps,
    bursts(ps).filter((_, i) => i !== index),
  );
}

// ---------------------------------------------------------------------------
// Sub-emitters

export type SubEmitterType = 'birth' | 'collision' | 'death';
export const SUB_EMITTER_TYPES: readonly SubEmitterType[] = ['birth', 'collision', 'death'];
export const INHERIT_OPTIONS = ['color', 'size', 'rotation', 'lifetime', 'velocity'] as const;

export interface SubEmitter {
  type?: SubEmitterType;
  emitter?: string;
  count?: number;
  probability?: number;
  inherit?: string[];
}

export function subEmitters(ps: ParticleSystem): SubEmitter[] {
  const list = moduleValue(ps, 'subEmitters')?.emitters;
  return Array.isArray(list) ? (list.filter(isObj) as SubEmitter[]) : [];
}

function withSubEmitters(ps: ParticleSystem, list: SubEmitter[]): ParticleSystem {
  const m: Obj = { ...(moduleValue(ps, 'subEmitters') ?? {}) };
  m.emitters = list;
  return { ...ps, subEmitters: m };
}

export function updateSubEmitter(ps: ParticleSystem, index: number, patch: Partial<SubEmitter>): ParticleSystem {
  return withSubEmitters(
    ps,
    subEmitters(ps).map((s, i) => {
      if (i !== index) return s;
      const next: Obj = { ...s, ...patch };
      for (const [k, v] of Object.entries(patch)) if (v === undefined || (Array.isArray(v) && v.length === 0)) delete next[k];
      return next as SubEmitter;
    }),
  );
}

/** Appends a sub-emitter entry (enabling the module). */
export function addSubEmitter(ps: ParticleSystem, entry: SubEmitter): ParticleSystem {
  const on = setModuleEnabled(ps, 'subEmitters', true);
  return withSubEmitters(on, [...subEmitters(on), entry]);
}

export function removeSubEmitter(ps: ParticleSystem, index: number): ParticleSystem {
  return withSubEmitters(
    ps,
    subEmitters(ps).filter((_, i) => i !== index),
  );
}

/** Entities that can be a sub-emitter of `id`: other entities with a particle system. */
export function subEmitterCandidates(doc: SceneDoc, id: string): EntityDesc[] {
  return doc.entities.filter((e) => e.id !== id && e.components && 'particleSystem' in e.components);
}

/**
 * Commands that add a new child entity with a one-shot particle system and
 * register it as a `type` sub-emitter of `parentId` (one undo step as a batch).
 */
export function newSubEmitterCommands(doc: SceneDoc, parentId: string, ps: ParticleSystem, type: SubEmitterType): { commands: EditorCommand[]; childId: string } {
  const taken = new Set(doc.entities.map((e) => e.id));
  const childId = uniqueWith(slug(`${parentId}_${type}`), (c) => taken.has(c));
  const child: EntityDesc = {
    id: childId,
    name: `${type[0]!.toUpperCase()}${type.slice(1)} Emitter`,
    parent: parentId,
    components: { particleSystem: clone(SUB_EMITTER_SYSTEM) },
  };
  const next = addSubEmitter(ps, { type, emitter: childId });
  return { commands: [{ cmd: 'addEntity', desc: child }, particleCommand(parentId, next)], childId };
}

// ---------------------------------------------------------------------------
// Undo coalescing

/**
 * Scene history after a gesture (a drag that applied many edits): every step
 * pushed since the gesture started collapses into the first one, so one undo
 * returns to `startDoc`. `past` holds the document before each step (the
 * scene store's convention); returns `past` itself when nothing to collapse.
 */
export function collapseHistory<D>(past: readonly D[], startDoc: D | null): readonly D[] {
  if (!startDoc) return past;
  const at = past.lastIndexOf(startDoc);
  if (at < 0 || at === past.length - 1) return past;
  return past.slice(0, at + 1);
}

// ---------------------------------------------------------------------------
// Isolated preview scene

export const PREVIEW_SCENE_PATH = '.prism/particle-preview.scene.json';
export const PREVIEW_GROUND_ID = '__preview_ground';
export const PREVIEW_SUN_ID = '__preview_sun';

/** Entity ids the preview of `id` needs: its subtree, sub-emitters (with theirs) and collision planes. */
export function previewEntityIds(doc: SceneDoc, id: string): string[] {
  const want = new Set<string>();
  const add = (eid: string) => {
    if (want.has(eid) || !doc.entities.some((e) => e.id === eid)) return;
    for (const d of subtreeIds(doc, eid)) {
      want.add(d);
      const ps = doc.entities.find((e) => e.id === d)?.components?.particleSystem;
      if (!isObj(ps)) continue;
      for (const s of subEmitters(ps)) if (s.emitter) add(s.emitter);
      const planes = moduleValue(ps, 'collision')?.planes;
      if (Array.isArray(planes)) for (const p of planes) if (typeof p === 'string' && doc.entities.some((e) => e.id === p)) want.add(p);
    }
  };
  add(id);
  return doc.entities.filter((e) => want.has(e.id)).map((e) => e.id);
}

/**
 * A scene with just the particle system `id` (and what it needs) on a
 * neutral stage: procedural sky, a directional light and a ground plane.
 * Roots keep their world transforms, moved so the system stands at the
 * origin (its height is kept, the ground is at y = 0).
 */
export function buildPreviewScene(doc: SceneDoc, id: string): SceneDoc {
  const ids = previewEntityIds(doc, id);
  const entities = copyEntities(doc, ids);
  const target = entities.find((e) => e.id === id);
  const at = target?.transform?.position ?? [0, 0, 0];
  const offset: [number, number, number] = [-(at[0] ?? 0), 0, -(at[2] ?? 0)];
  for (const e of entities) {
    if (e.parent) continue;
    const p = e.transform?.position ?? [0, 0, 0];
    e.transform = { ...(e.transform ?? {}), position: [p[0] + offset[0], p[1], p[2] + offset[2]] };
    // Scripts do not run in edit mode, cameras would only add gizmos.
    if (e.components) delete e.components.camera;
  }
  return {
    format: 'prism.scene/1',
    name: 'Particle Preview',
    environment: { skybox: { type: 'procedural' }, ambient: { source: 'skybox', intensity: 1 } },
    entities: [
      {
        id: PREVIEW_SUN_ID,
        name: 'Preview Light',
        transform: { rotation: [-50, -30, 0] },
        components: { light: { kind: 'directional', color: [1, 0.97, 0.92], intensity: 1.6 } },
      },
      {
        id: PREVIEW_GROUND_ID,
        name: 'Preview Ground',
        transform: { scale: [24, 1, 24] },
        components: { mesh: { source: 'primitive:plane' } },
      },
      ...entities.filter((e) => e.id !== PREVIEW_SUN_ID && e.id !== PREVIEW_GROUND_ID),
    ],
  };
}

/**
 * How to bring a live preview from `prev` to `next`: component edits of the
 * same entities become `setComponent` / `removeComponent` commands (the
 * running particles survive); anything structural (entities, parents,
 * transforms, names, environment) reloads the preview scene. Null = no change.
 */
export function previewSync(prev: SceneDoc, next: SceneDoc): { reload: true } | { reload: false; commands: EditorCommand[] } | null {
  if (prev.entities.length !== next.entities.length) return { reload: true };
  const commands: EditorCommand[] = [];
  for (let i = 0; i < next.entities.length; i++) {
    const a = prev.entities[i]!;
    const b = next.entities[i]!;
    if (a.id !== b.id || (a.parent ?? null) !== (b.parent ?? null) || JSON.stringify(a.transform ?? {}) !== JSON.stringify(b.transform ?? {}))
      return { reload: true };
    const ca = a.components ?? {};
    const cb = b.components ?? {};
    for (const key of new Set([...Object.keys(ca), ...Object.keys(cb)])) {
      if (JSON.stringify(ca[key]) === JSON.stringify(cb[key])) continue;
      if (cb[key] === undefined) commands.push({ cmd: 'removeComponent', id: b.id, key });
      else commands.push({ cmd: 'setComponent', id: b.id, key, value: cb[key] });
    }
  }
  return commands.length ? { reload: false, commands } : null;
}
